import React from 'react';
import type { AlertTypeRow } from '../../../types/tripsMonitoredUsage';
import { type AlertFilterType } from '../../../types/alertFilter';
import { formatNumber } from '../utils/formatNumber';
import { useTableSort } from '../hooks/useTableSort';
import TableHeader from './TableHeader';
import AlertTypeFilterDropdown from './AlertTypeFilterDropdown';
import SortableColumnHeader from './SortableColumnHeader';
import tableStyles from '../../../components/table/styles.module.css';
import styles from './alertsByTypeTable.module.css';

interface AlertsByTypeTableProps {
  data: AlertTypeRow[];
  periodColumns: string[];
  filterType: AlertFilterType;
  onFilterChange: (type: AlertFilterType) => void;
  availableChannels: AlertFilterType[];
  onExport: () => void;
}

const AlertsByTypeTable: React.FC<AlertsByTypeTableProps> = ({
  data,
  periodColumns,
  filterType,
  onFilterChange,
  availableChannels,
  onExport,
}) => {
  // Rows are already filtered by channel upstream; sort what we're given.
  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(data);

  return (
    <div className={styles.wrapper}>
      <TableHeader
        title="Alerts by alert type"
        onExport={onExport}
        titleAddon={
          <AlertTypeFilterDropdown
            value={filterType}
            onChange={onFilterChange}
            availableChannels={availableChannels}
          />
        }
      />
      <div className={styles.scrollArea}>
        <table className={`${tableStyles.table} ${tableStyles.tableStriped}`}>
          <thead className={tableStyles.tableHead}>
            <tr>
              <SortableColumnHeader
                label="Alert Type"
                columnKey="alertType"
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                className={styles.firstCol}
                iconPosition="right"
              />
              {periodColumns.map(col => (
                <SortableColumnHeader
                  key={col}
                  label={col}
                  columnKey={col}
                  sortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  className={styles.periodHeader}
                />
              ))}
            </tr>
          </thead>
          <tbody className={tableStyles.tbody}>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={periodColumns.length + 1} className={tableStyles.noRowsValue}>
                  No data available
                </td>
              </tr>
            ) : (
              sortedData.map(row => (
                <tr key={row.alertType}>
                  <td className={styles.firstCol}>{row.alertType}</td>
                  {periodColumns.map(col => (
                    <td key={col}>
                      {typeof row[col] === 'number'
                        ? formatNumber(row[col] as number)
                        : (row[col] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AlertsByTypeTable;
